import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import alertsService from '../../services/alertsService'
import { addAuditLog } from './auditSlice'

// ── Thunks ─────────────────────────────────────────────────────────────────

export const fetchAlerts = createAsyncThunk('alerts/fetchList',
  async (params, { rejectWithValue }) => {
    try { return await alertsService.getList(params) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchAlertById = createAsyncThunk('alerts/fetchById',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const data = await alertsService.getById(id)
      dispatch(addAuditLog({ action: 'ALERT_VIEWED', subject: 'Alert', meta: { alertId: id } }))
      return data
    }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const assignAlert = createAsyncThunk('alerts/assign',
  async ({ id, assignee }, { dispatch, rejectWithValue }) => {
    try {
      const data = await alertsService.assign(id, assignee)
      dispatch(addAuditLog({ action: 'ALERT_ASSIGNED', subject: 'Alert', meta: { alertId: id, assignee } }))
      return data
    }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const decideAlert = createAsyncThunk('alerts/decide',
  async ({ id, decision, comment }, { dispatch, rejectWithValue }) => {
    try {
      const data = await alertsService.decide(id, decision, comment)
      dispatch(addAuditLog({ action: `ALERT_${decision}`, subject: 'Alert', meta: { alertId: id } }))
      return data
    }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const escalateAlert = createAsyncThunk('alerts/escalate',
  async ({ id, reason }, { dispatch, rejectWithValue }) => {
    try {
      const data = await alertsService.escalate(id, reason)
      dispatch(addAuditLog({ action: 'ALERT_ESCALATED', subject: 'Alert', meta: { alertId: id } }))
      return data
    }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const addAlertComment = createAsyncThunk('alerts/addComment',
  async ({ id, comment }, { rejectWithValue }) => {
    try { return await alertsService.addComment(id, comment) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchInvestigation = createAsyncThunk('alerts/fetchInvestigation',
  async (params, { rejectWithValue }) => {
    try { return await alertsService.getInvestigation(params) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

// ── Slice ──────────────────────────────────────────────────────────────────

const alertsSlice = createSlice({
  name: 'alerts',
  initialState: {
    list:          [],
    total:         0,
    selected:      null,
    investigation: null,
    loading: {
      list: false, detail: false, action: false, investigation: false,
    },
    errors: {},
    filters: {
      status:   '',
      priority: '',
      owner:    '',
      team:     '',
      search:   '',
      risk_level: '',
      source: '',
      dateFrom: '',
      dateTo: '',
    },
    savedViews: [
      { id: 'v1', name: 'My open alerts',   filters: { status: 'OPEN',      owner: 'me' } },
      { id: 'v2', name: 'Unassigned',       filters: { status: 'OPEN',      owner: '' } },
      { id: 'v3', name: 'Critical priority',filters: { priority: 'CRITICAL', status: '' } },
      { id: 'v4', name: 'SLA breached',     filters: { sla_breached: true,   status: '' } },
    ],
  },
  reducers: {
    setAlertFilter(state, { payload }) {
      state.filters = { ...state.filters, ...payload }
    },
    resetAlertFilters(state) {
      state.filters = { status: '', priority: '', owner: '', team: '', search: '', risk_level: '', source: '', dateFrom: '', dateTo: '',
      }
    },
    replaceAlertFilters(state, { payload }) {
      state.filters = payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlerts.pending,   s     => { s.loading.list = true;  s.errors.list = null })
      .addCase(fetchAlerts.rejected,  (s,a) => { s.loading.list = false; s.errors.list = a.payload })
      .addCase(fetchAlerts.fulfilled, (s,a) => { s.loading.list = false; s.list = a.payload.data; s.total = a.payload.total })

    builder
      .addCase(fetchAlertById.pending,   s     => { s.loading.detail = true;  s.errors.detail = null })
      .addCase(fetchAlertById.rejected,  (s,a) => { s.loading.detail = false; s.errors.detail = a.payload })
      .addCase(fetchAlertById.fulfilled, (s,a) => { s.loading.detail = false; s.selected = a.payload })

    builder
      .addCase(assignAlert.fulfilled, (s,a) => {
        s.list = s.list.map(al => al.id === a.payload.id ? { ...al, owner: a.payload.owner } : al)
        if (s.selected?.id === a.payload.id) s.selected = { ...s.selected, owner: a.payload.owner }
      })

    builder
      .addCase(decideAlert.pending,   s     => { s.loading.action = true })
      .addCase(decideAlert.fulfilled, (s,a) => {
        s.loading.action = false
        s.list    = s.list.map(al => al.id === a.payload.id ? { ...al, ...a.payload } : al)
        if (s.selected?.id === a.payload.id) s.selected = { ...s.selected, ...a.payload }
      })

    builder
      .addCase(escalateAlert.fulfilled, (s,a) => {
        s.list = s.list.map(al => al.id === a.payload.id ? { ...al, ...a.payload } : al)
        if (s.selected?.id === a.payload.id) s.selected = { ...s.selected, ...a.payload }
      })

    builder
      .addCase(addAlertComment.fulfilled, (s,a) => {
        if (s.selected?.id === a.payload.alertId)
          s.selected = { ...s.selected, comments: [...(s.selected.comments || []), a.payload.comment] }
      })

    builder
      .addCase(fetchInvestigation.pending,   s     => { s.loading.investigation = true })
      .addCase(fetchInvestigation.fulfilled, (s,a) => { s.loading.investigation = false; s.investigation = a.payload })
  },
})

export const { setAlertFilter, resetAlertFilters, replaceAlertFilters, } = alertsSlice.actions

export const selectAlertList = s => s.alerts?.list ?? []
export const selectAlertTotal = s => s.alerts?.total ?? 0
export const selectSelectedAlert = s => s.alerts?.selected ?? null
export const selectAlertFilters = s => s.alerts?.filters ?? {}
export const selectAlertsLoading = s => s.alerts?.loading ?? {}
export const selectAlertSavedViews = s => s.alerts?.savedViews ?? []
export const selectInvestigation = s => s.alerts?.investigation ?? null

export default alertsSlice.reducer